// Подготовка ячеек «где суда замолкают» к отрисовке на карте.
//
// Бэкенд отдаёт ЮГО-ЗАПАДНЫЙ угол ячейки и её сторону в градусах (а не центр):
// ошибка на половину градуса невидима глазом, но сдвигает картину на ~55 км.

import type { ObsCell } from '../api'

export interface CellFeatureCollection {
  type: 'FeatureCollection'
  features: {
    type: 'Feature'
    geometry: { type: 'Polygon'; coordinates: number[][][] }
    properties: { gaps: number; vessels: number; blind_hours: number }
  }[]
}

/** Ячейки → полигоны GeoJSON (кольцо замкнуто: MapLibre иначе не рисует). */
export function cellsToGeoJSON(cells: ObsCell[]): CellFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: cells.map(cell => {
      const { lat, lon, size_deg: size } = cell
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: [[
            [lon, lat],
            [lon + size, lat],
            [lon + size, lat + size],
            [lon, lat + size],
            [lon, lat],
          ]],
        },
        properties: {
          gaps: cell.gaps,
          vessels: cell.vessels,
          blind_hours: cell.blind_hours,
        },
      }
    }),
  }
}

// Заливка: от прозрачно-синей к янтарю. Не «зелёный→красный»: раздел описывает
// факт молчания, а не оценивает его — светофор читался бы как обвинение.
const COLORS = ['#1b3a52', '#2b6a8f', '#4d8fb0', '#9ab84f', '#d9a441']

export interface Ramp {
  max: number
  stops: [number, string][]
  /** Границы цветов для легенды — те же значения, что в stops (без нуля). */
  breaks: number[]
}

// Доли распределения, на которых меняется цвет. Не равные интервалы: часы
// молчания скошены чудовищно (порт стоянки набирает десятки тысяч часов, обычная
// промысловая ячейка — единицы), и линейная шкала красит 99% карты первым цветом.
const QUANTILES = [0.5, 0.8, 0.93, 1]

function quantile(sorted: number[], q: number): number {
  if (!sorted.length) return 0
  const pos = (sorted.length - 1) * q
  const lo = Math.floor(pos)
  const hi = Math.ceil(pos)
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo)
}

/**
 * Шкала заливки по часам молчания в ячейке — ПО КВАНТИЛЯМ данных.
 *
 * Стопы гарантированно строго возрастают: у скошенного распределения соседние
 * квантили сплошь и рядом совпадают, а неубывающие стопы роняют `interpolate`
 * MapLibre целиком, а не деградируют в один цвет.
 */
export function blindHoursRamp(cells: ObsCell[]): Ramp {
  const values = cells.map(c => c.blind_hours).sort((a, b) => a - b)
  const max = values.length ? values[values.length - 1] : 0

  const stops: [number, string][] = [[0, COLORS[0]]]
  let previous = 0
  QUANTILES.forEach((q, i) => {
    const raw = values.length ? quantile(values, q) : (i + 1) / QUANTILES.length
    // Строго возрастаем: равные квантили разводим минимальным шагом.
    const value = raw > previous ? raw : previous + Math.max(previous * 1e-6, 1e-6)
    stops.push([value, COLORS[i + 1]])
    previous = value
  })

  return { max, stops, breaks: stops.slice(1).map(([value]) => value) }
}
